import { Button, Popconfirm, Space, Table, Tag, notification } from "antd";
import {
  DeleteTwoTone,
  EditTwoTone,
  FolderOpenOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import React, { useCallback } from "react";
import { useEffect, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";

import {
  deleteProjectById,
  toggleArchivedById,
  togglePublishedById,
} from "../../store/api/projects";
import AddProjectPopup from "./AddProjectPopup";
import { config } from "../../config";
import { PROJECT_STATUS_LABELS } from "../../constants";
import ProjectsToolbar from "./ProjectsToolbar";

const ProjectsTable = ({ projects, handleFetch, loading, total }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const history = useHistory();

  const [filters, setFilters] = useState({
    status: "all",
    startDate: null,
    endDate: null,
    searchKeyword: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [confirmLoading, setConfirmLoading] = useState(false);

  const refetchData = useCallback(() => {
    handleFetch({
      ...filters,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [filters, pagination, handleFetch]);

  useEffect(() => {
    handleFetch({
      ...filters,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [filters, pagination, handleFetch]);

  const handleFiltersChange = (filterKey, filterValue) => {
    setFilters((prev) => ({ ...prev, [filterKey]: filterValue }));
    setPagination({
      current: 1,
      pageSize: pagination.pageSize,
    });
  };

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleArchive = (item) => {
    setConfirmLoading(true);
    toggleArchivedById(item.id)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({ message: data.message });
        refetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const handleDelete = (item) => {
    setConfirmLoading(true);
    deleteProjectById(item.id)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({ message: data.message });
        refetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const changeStatus = (item) => {
    setConfirmLoading(true);
    togglePublishedById(item.id)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({ message: data.message });
        refetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const closePopup = () => {
    setIsModalVisible(false);
    setActiveItem(null);
  };

  const onUpdateSuccess = () => {
    refetchData();
    setActiveItem(null);
  };

  const renderStatus = (status) => {
    const colors = {
      PENDING: "blue",
      ONGOING: "cyan",
      IN_REVIEW: "purple",
      COMPLETED: "green",
      OTHERS: "orange",
    };

    return <Tag color={colors[status]}>{PROJECT_STATUS_LABELS[status]}</Tag>;
  };

  const columns = [
    {
      title: "Created Date",
      dataIndex: "dates",
      render: (_, record) => {
        return (
          <p>
            {" "}
            {new Date(_.created_at).toLocaleDateString("en-GB", {
              weekday: "short",
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </p>
        );
      },
      width: "150px",
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (_, record) => {
        return <NavLink to={`/projects/${record.id}`}>{_}</NavLink>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => {
        return (
          <>
            <Space>{_ ? renderStatus(_) : "N/A"}</Space>
          </>
        );
      },
      width: "150px",
    },
    {
      title: "Project Date",
      dataIndex: "projectDate",
      render: (_, record) => {
        return (
          <p>
            {_
              ? new Date(_).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "-"}
          </p>
        );
      },
      width: "150px",
    },

    {
      title: "Thumbnail",
      dataIndex: "filePath",
      render: (value, record) => {
        return value ? (
          <img
            src={`${config.filesBaseUrl}/${value}`}
            alt={record.title}
            width={80}
          />
        ) : (
          "-"
        );
      },
      width: "80px",
    },
    // {
    //   title: "Published By",
    //   dataIndex: "publishedBy",
    //   render: (_, record) => {
    //     return <p> {_?.email}</p>;
    //   },
    // },
    {
      title: "Published on www",
      dataIndex: "isPublished",
      render: (_, record) => {
        return (
          <>
            <Space>
              {_ ? (
                <Tag color="green" style={{ width: 40 }}>
                  Yes
                </Tag>
              ) : (
                <Tag color="red" style={{ width: 40 }}>
                  No
                </Tag>
              )}
              <Popconfirm
                title={
                  record.isPublished
                    ? "Are you sure want to unpublish this project?"
                    : "Are you sure want to publish this project?"
                }
                onConfirm={() => changeStatus(record)}
              >
                <Button style={{ width: "100px" }} type="default">
                  {_ ? "Unpublish" : "Publish"}
                </Button>
              </Popconfirm>
            </Space>
          </>
        );
      },
      width: "200px",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: "100px",
      render: (_, record) => {
        return (
          <Space style={{ justifyContent: "center", width: "100%" }}>
            <EditTwoTone
              title="Edit"
              twoToneColor="#1890ff"
              style={{
                fontSize: "20px",
                cursor: "pointer",
                marginRight: "8px",
              }}
              onClick={() => {
                setActiveItem(record);
                setIsModalVisible(true);
              }}
            />
            <Popconfirm
              confirmLoading={confirmLoading}
              title={
                record.isArchived
                  ? "Are you sure you want to unarchive this project?"
                  : "Are you sure want to archive this project?"
              }
              onConfirm={() => handleArchive(record)}
              placement="topRight"
            >
              {record.isArchived ? (
                <UndoOutlined
                  title="Unarchive"
                  style={{
                    color: "gray",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                />
              ) : (
                <FolderOpenOutlined
                  title="Archive"
                  style={{
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "gray",
                  }}
                />
              )}
            </Popconfirm>
            <Popconfirm
              confirmLoading={confirmLoading}
              title="Are you sure want to delete this project?"
              onConfirm={() => handleDelete(record)}
              placement="topRight"
            >
              <DeleteTwoTone
                title="Delete"
                twoToneColor="#ff0000"
                style={{
                  fontSize: "20px",
                  cursor: "pointer",
                }}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      {isModalVisible && (
        <AddProjectPopup
          closePopup={closePopup}
          item={activeItem}
          onCreateSuccess={(data) => history.push(`/projects/${data.id}`)}
          onUpdateSuccess={onUpdateSuccess}
        />
      )}
      <ProjectsToolbar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onAddProject={() => setIsModalVisible(true)}
      />
      <Table
        dataSource={projects}
        columns={columns}
        loading={loading}
        bordered
        rowKey="id"
        pagination={{
          pageSizeOptions: ["10", "20", "50"],
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
        }}
        onChange={handleTableChange}
      />
    </>
  );
};

export default ProjectsTable;
